import React from 'react';
import { useTranslation } from 'react-i18next';
import { PermissionGuard, SidePanelFormLayout } from '@/components/layout';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { LabelInput } from '@/components/ui/inputs/LabelInput';
import { Selector } from '@/components/ui/inputs/selector';
import { TextArea } from '@/components/ui/inputs/TextArea';
import {
  usePositionFormPanel,
  type PositionFormRecord,
} from '@/hooks/organization/position/usePositionFormPanel';


interface PositionFormPanelProps {
  onSubmit?: () => void;
  readOnly?: boolean;
  record?: PositionFormRecord;
}

export const PositionFormPanel: React.FC<PositionFormPanelProps> = ({
  onSubmit,
  readOnly,
  record,
}) => {
  const { t: translate } = useTranslation();

  const {
    codeValue,
    nameValue,
    descriptionValue,
    areaValue,
    areaOptions,
    metadata,
    isEditMode,
    effectiveReadOnly,
    isSubmitting,
    isReady,
    canSave,
    handleCodeChange,
    handleNameChange,
    handleDescriptionChange,
    handleAreaChange,
    handleSubmit,
    errors,
    isSubmitted,
  } = usePositionFormPanel({ onSubmit, readOnly, record });

  return (
    <SidePanelFormLayout onSubmit={handleSubmit} data-testid="position-form-panel">
      <SidePanelFormLayout.Fields>
        {isEditMode && metadata && (
          <LabelInput
            label={translate('organization:fields.id')}
            value={metadata.id}
            disabled
          />
        )}

        <LabelInput
          label={translate('organization:fields.positionName')}
          value={nameValue}
          onChange={handleNameChange}
          required
          disabled={effectiveReadOnly || isSubmitting}
          color={errors.name && isSubmitted ? 'error' : 'default'}
          supportingText={
            errors.name?.message && isSubmitted
              ? translate(String(errors.name.message))
              : undefined
          }
          data-testid="position-name-input"
        />

        <Selector
          label={translate('organization:fields.area')}
          required
          options={areaOptions}
          value={areaValue}
          onChange={handleAreaChange}
          disabled={effectiveReadOnly || isSubmitting}
          color={errors.areaId && isSubmitted ? 'error' : 'default'}
          supportingText={
            errors.areaId?.message && isSubmitted
              ? translate(String(errors.areaId.message))
              : undefined
          }
          data-testid="position-area-selector"
        />

        <LabelInput
          label={translate('organization:fields.positionCode')}
          value={codeValue}
          onChange={handleCodeChange}
          required
          disabled={effectiveReadOnly || isSubmitting}
          color={errors.code && isSubmitted ? 'error' : 'default'}
          supportingText={
            errors.code?.message && isSubmitted
              ? translate(String(errors.code.message))
              : undefined
          }
          data-testid="position-code-input"
        />

        <TextArea
          label={translate('organization:fields.description')}
          value={descriptionValue}
          onChange={handleDescriptionChange}
          required
          disabled={effectiveReadOnly || isSubmitting}
          color={errors.description && isSubmitted ? 'error' : 'default'}
          supportingText={
            errors.description?.message && isSubmitted
              ? translate(String(errors.description.message))
              : undefined
          }
          maxLength={8000}
          rows={5}
          showCharacterCount
          data-testid="position-description-input"
        />

        {isEditMode && metadata && (
          <>
            <LabelInput
              label={translate('common:fields.createdDate')}
              value={metadata.createdDate}
              disabled
            />

            <LabelInput
              label={translate('common:fields.createdBy')}
              value={metadata.createdBy}
              disabled
            />

            <LabelInput
              label={translate('common:fields.modifiedDate')}
              value={metadata.modifiedDate}
              disabled
            />

            <LabelInput
              label={translate('common:fields.modifiedBy')}
              value={metadata.modifiedBy}
              disabled
            />
          </>
        )}
      </SidePanelFormLayout.Fields>

      {!effectiveReadOnly && (
        <SidePanelFormLayout.Footer>
          <PermissionGuard
            action={isEditMode ? 'edit' : 'create'}
            denied={!canSave}
          >
            <PrimaryButton type="submit" fullWidth isLoading={isSubmitting} disabled={!isReady || isSubmitting}>
              {translate(
                isEditMode
                  ? 'common:actions.save'
                  : 'organization:actions.registerPosition',
              )}
            </PrimaryButton>
          </PermissionGuard>
        </SidePanelFormLayout.Footer>
      )}
    </SidePanelFormLayout>
  );
};

export default PositionFormPanel;